import React, { useMemo } from 'react';

import {
    Alert,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassAccessSettings from './ClassAccess';
import OverrideControls from './OverrideControls';
import SuperAdminTools from './SuperAdminTools';
import {
    ClassAccess,
    ClassLevelOverrideOptions,
    ScreenShareResolutions,
    WebExpOptions
} from '../../../types/adminConfig';
import { ConfigIngestModes, Permission } from '../../../types/recordingTypes';
import AdminAuthContainer from '../AdminAuthContainer';
import AdminSettingsContainer from '../AdminSettingsContainer';
import SaveButton from '../components/SaveButton';
import StyledFormTitle from '../styles/form-title';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';
import { showNonManagedZoomSetting } from '../utils/showAdvancedSetting';

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

const StyledFormGroup = styled(FormGroup)`
    max-width: 400px;
`;

const StyledFormGroupContainer = styled.div`
    margin-left: 10px;
    margin-bottom: 1rem;
`;

const StyledHelperLink = styled.a`
    margin-bottom: 10px;
    margin-left: 30px;
    font-size: 14px;
    text-decoration: underline;
`;

const Spacer = styled.div`
    margin: 1rem;
`;

const SuperAdmin = () => {
    const { t } = useTranslation('admin_settings');
    const { isSuperAdmin, isAdmin } = useContainer(AdminAuthContainer);
    const {
        adminSettings,
        setAdminSettings,
        isSavingSettingsData,
        handleSaveSettings,
        setImportRecordings,
        school,
        loadingSchool,
        isSavingDisabled
    } = useContainer(AdminSettingsContainer);
    const { featureSettings, authSettings, classForTeamsSettings } = adminSettings;
    const { options, overrides } = featureSettings;

    const { webExperience = WebExpOptions.NextGen, allowVanillaZoom = true } = options;

    const isTeams = school?.isTeams;

    // Apply default of no-one to org screenshare res.
    // TODO - Not sure if we should also be doing this for other settings, otherwise saving with a setting
    // of "no one" will result in no override being generated unless you first change it to something else.
    if (!isTeams && !overrides.orgScreenshareRes) {
        overrides.orgScreenshareRes = ClassLevelOverrideOptions.NoOne;
    }

    const includeRestrictGalleryOption = !isTeams;

    const handleFeatureSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        options[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateMobileSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === 'true';
        options[event.target.name] = value;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOverrides = (event: React.ChangeEvent<HTMLInputElement>) => {
        overrides[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsBool = (event: React.ChangeEvent<HTMLInputElement>) => {
        options[event.target.id] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsSelect = (
        event: SelectChangeEvent<ClassAccess | Permission | ConfigIngestModes | ScreenShareResolutions>
    ) => {
        options[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleSelectSettingsChange = (
        event: SelectChangeEvent<ClassAccess | Permission | ConfigIngestModes | ScreenShareResolutions>
    ) => {
        featureSettings[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleImportRecordingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setImportRecordings(event.target.checked);
    };

    const handleAuthSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id || event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleDomainsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.id || event.target.name] = event.target.value
            .split(',')
            .map((d) => d?.trim().toLowerCase());
        setAdminSettings({ ...adminSettings });
    };

    const handleClassForTeamsSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        classForTeamsSettings[event.target.id || event.target.name] = Number(event.target.value);
        setAdminSettings({ ...adminSettings });
    };

    const nonManagedZoomSettingShown = useMemo(
        () => showNonManagedZoomSetting({ school, loadingSchool, isSuperAdmin }),
        [school, loadingSchool, isSuperAdmin]
    );

    if (!isSuperAdmin) {
        return <Alert severity="warning">{t('version-management.permissions-error')}</Alert>;
    }

    const featureFlags = [
        {
            name: 'salesTrainingMode',
            label: 'configuration.sales-training-mode',
            options: true,
            condition: !school?.isCollab
        },
        { name: 'hasPanicMode', label: 'user-access.allow-panic-mode' },
        { name: 'enableLogAutoUpload', label: 'user-access.enable-log-auto-upload' },
        { name: 'allowInstructorSwap', label: 'configuration.allowInstructorSwap' },
        { name: 'classNotes', label: 'configuration.classNotes' },
        { name: 'recordingParticipantsCanView', label: 'user-access.recording-participants-can-view' },
        { name: 'restrictSessionsRecordingsByGroups', label: 'user-access.restrict-sessions-recordings-by-groups' },
        {
            name: 'recordingRestrictGallery',
            label: 'configuration.recordings.restrict-gallery',
            condition: includeRestrictGalleryOption
        },
        { name: 'recordingLegacyZoomList', label: 'configuration.recordings.zoom-legacy', condition: !isTeams },
        {
            name: 'disableTeamsRecording',
            label: 'configuration.recordings.disable',
            options: true,
            condition: isTeams
        },
        { name: 'enableBreakoutRoomCloudRecording', label: 'configuration.enable-breakout-room-cloud-recording' },
        { name: 'preventLeaveSessionRedirect', label: 'configuration.prevent-leave-session-redirect' },
        { name: 'whiteboardScreenshotFeature', label: 'configuration.whiteboard-screenshot-feature' },
        { name: 'instructorChatDownload', label: 'configuration.instructor-chat-download' },
        { name: 'isBbData', label: 'configuration.is-bb-data' },
        { name: 'dataPortalAdmin', label: 'configuration.data-portal-admin' },
        { name: 'dataPortalBusinessAnalyst', label: 'configuration.data-portal-business-analyst' },
        { name: 'dataPortalClassBusinessAnalyst', label: 'configuration.data-portal-class-business-analyst' },
        { name: 'dataPortalCustomerSupport', label: 'configuration.data-portal-customer-support' },
        { name: 'dataPortalInstructor', label: 'configuration.data-portal-instructor' },
        { name: 'dataPortalStudent', label: 'configuration.data-portal-student' },
        { name: 'allowAiForSupport', label: 'configuration.allow-ai-for-support' },
        { name: 'allowAiForInsights', label: 'configuration.allow-ai-for-insights' },
        {
            name: 'forceWebAsChromebook',
            label: 'configuration.force-web-as-chromebook',
            options: true,
            hint: 'configuration.force-web-as-chromebook-hint'
        },
        {
            name: 'subClasses',
            label: 'configuration.allowSubClassesInfo',
            hint: 'configuration.allowSubClassesHint',
            options: true
        }
    ];

    return (
        <>
            <Title>{t('navigationTabs.super-admin')}</Title>

            <StyledFormGroupContainer>
                <FormGroup>
                    {featureFlags
                        .filter((f) => f.condition ?? true)
                        .map((flag) => (
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={(flag.options ? options : featureSettings)[flag.name]}
                                        onChange={flag.options ? handleUpdateOptionsBool : handleFeatureSettingsChange}
                                        id={flag.name}
                                        name={flag.name}
                                    />
                                }
                                label={t(flag.label)}
                                hint={flag.hint ? t(flag.hint) : undefined}
                                key={flag.name}
                            />
                        ))}
                </FormGroup>

                <FormGroup sx={{ mb: 2 }}>
                    <FormLabel id="class-access-select-label" sx={{ color: 'inherit' }}>
                        {t('user-access.class-access.header')}
                    </FormLabel>
                    <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                        <Select
                            labelId="class-access-select-label"
                            id="classAccess"
                            name="classAccess"
                            value={featureSettings.classAccess}
                            onChange={handleSelectSettingsChange}
                        >
                            <MenuItem value={ClassAccess.All}>{t('user-access.class-access.options.all')}</MenuItem>
                            <MenuItem value={ClassAccess.App}>{t('user-access.class-access.options.app')}</MenuItem>
                            <MenuItem value={ClassAccess.Lti}>{t('user-access.class-access.options.lti')}</MenuItem>
                            <MenuItem value={ClassAccess.None}>{t('user-access.class-access.options.none')}</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>

                <FormGroup sx={{ mb: 2 }}>
                    <FormLabel id="attendance-access-select-label" sx={{ color: 'inherit' }}>
                        {t('user-access.attendance-participants-allow')}
                    </FormLabel>
                    <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                        <Select
                            labelId="attendance-access-select-label"
                            id="attendanceTabAccess"
                            name="attendanceTabAccess"
                            value={featureSettings.attendanceTabAccess}
                            onChange={handleSelectSettingsChange}
                        >
                            <MenuItem value={ClassAccess.All}>{t('user-access.class-access.options.all')}</MenuItem>
                            <MenuItem value={ClassAccess.App}>{t('user-access.class-access.options.app')}</MenuItem>
                            <MenuItem value={ClassAccess.Lti}>{t('user-access.class-access.options.lti')}</MenuItem>
                            <MenuItem value={ClassAccess.None}>{t('user-access.class-access.options.none')}</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>

                <FormGroup>
                    <FormLabel id="recordings-access-select-label" sx={{ color: 'inherit' }}>
                        {t('user-access.recordings-tab-access')}
                    </FormLabel>
                    <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                        <Select
                            labelId="recordings-access-select-label"
                            id="recordingTabAccess"
                            name="recordingTabAccess"
                            value={featureSettings.recordingTabAccess}
                            onChange={handleSelectSettingsChange}
                        >
                            <MenuItem value={ClassAccess.All}>{t('user-access.class-access.options.all')}</MenuItem>
                            <MenuItem value={ClassAccess.App}>{t('user-access.class-access.options.app')}</MenuItem>
                            <MenuItem value={ClassAccess.Lti}>{t('user-access.class-access.options.lti')}</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>

                <FormGroup sx={{ mt: 1 }}>
                    <FormLabel id="recordings-permission-label" sx={{ color: 'inherit' }}>
                        {t('configuration.recordings.permission')}
                    </FormLabel>
                    <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                        <Select
                            labelId="recordings-permission-select-label"
                            id="recordingInitialPermission"
                            name="recordingInitialPermission"
                            value={featureSettings.recordingInitialPermission}
                            onChange={handleSelectSettingsChange}
                        >
                            <MenuItem value={Permission.ADMINS}>{t('lti:recordings.permissions.admins')}</MenuItem>
                            <MenuItem value={Permission.ROSTER}>{t('lti:recordings.permissions.roster')}</MenuItem>
                            <MenuItem value={Permission.PUBLIC}>{t('lti:recordings.permissions.public')}</MenuItem>
                        </Select>
                    </FormControl>
                </FormGroup>

                <FormGroup sx={{ mt: 2 }}>
                    <FormLabel id="recordings-ingest-label" sx={{ color: 'inherit' }}>
                        {t('configuration.recordings.ingest-mode')}
                    </FormLabel>
                    <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                        <Select
                            labelId="recordings-ingest-select-label"
                            id="recordingIngestMode"
                            name="recordingIngestMode"
                            value={featureSettings.recordingIngestMode}
                            onChange={handleSelectSettingsChange}
                        >
                            <MenuItem value={ConfigIngestModes.EXCLUDE}>
                                {t('configuration.recordings.ingest-modes.exclude')}
                            </MenuItem>
                            {!isTeams && (
                                <MenuItem value={ConfigIngestModes.TRACK}>
                                    {t('configuration.recordings.ingest-modes.track')}
                                </MenuItem>
                            )}
                            <MenuItem value={ConfigIngestModes.COPY}>
                                {t(
                                    isTeams
                                        ? 'configuration.recordings.ingest-modes.copy-teams'
                                        : 'configuration.recordings.ingest-modes.copy'
                                )}
                            </MenuItem>
                            {isTeams && (
                                <MenuItem value={ConfigIngestModes.MOVE}>
                                    {t('configuration.recordings.ingest-modes.move')}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </FormGroup>

                <FormGroup sx={{ mb: 2, ml: 4 }}>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                color="primary"
                                onChange={handleImportRecordingsChange}
                                id="importRecordings"
                                name="importRecordings"
                            />
                        }
                        label={t('configuration.recordings.import')}
                    />
                </FormGroup>

                {!isTeams && (
                    <FormGroup sx={{ mb: 3 }}>
                        <FormLabel id="org-screenshare-res-label" sx={{ color: 'inherit' }}>
                            {t('configuration.orgScreenshareRes')}
                        </FormLabel>
                        <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                            <Select
                                value={featureSettings.options.orgScreenshareRes}
                                onChange={handleUpdateOptionsSelect}
                                id="orgScreenshareRes"
                                name="orgScreenshareRes"
                                labelId="org-screenshare-res-label"
                            >
                                {/* DISABLED due to flickering at this res on windows
                                    <MenuItem value={ScreenShareResolutions.Low}>
                                        {t('screenshare.resolutions.low')}
                                    </MenuItem> */}

                                <MenuItem value={ScreenShareResolutions.Medium}>
                                    {t('screenshare.resolutions.medium')}
                                </MenuItem>

                                <MenuItem value={ScreenShareResolutions.High}>
                                    {t('screenshare.resolutions.high')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </FormGroup>
                )}

                <FormGroup sx={{ mb: 2 }}>
                    <FormLabel id="web-experience-label">{t('configuration.web-experience')}</FormLabel>

                    <RadioGroup
                        aria-labelledby="web-experience-label"
                        name="webExperience"
                        value={webExperience}
                        onChange={handleUpdateOptionsRadio}
                    >
                        <FormControlLabel value="classic" control={<Radio />} label={t('configuration.classic')} />
                        <FormControlLabel value="nextgen" control={<Radio />} label={t('configuration.nextgen')} />
                    </RadioGroup>
                </FormGroup>
                <OverrideControls
                    overrideName="webExperience"
                    overrideValue={overrides.webExperience ?? ClassLevelOverrideOptions.NoOne}
                    handleUpdateOverrides={handleUpdateOverrides}
                    labelText={t('configuration.web-experience-override')}
                    tooltipText={t('configuration.no-one-web-experience-tooltip')}
                />

                <FormGroup>
                    <FormLabel>{t('configuration.allow-mobile')}</FormLabel>

                    <RadioGroup
                        aria-labelledby="mobile-experience-label"
                        name="allowNextGenMobile"
                        id="allowNextGenMobile"
                        value={featureSettings.options.allowNextGenMobile}
                        onChange={handleUpdateMobileSettings}
                    >
                        <FormControlLabel value={false} control={<Radio />} label={t('configuration.classic')} />
                        <FormControlLabel value={true} control={<Radio />} label={t('configuration.nextgen-mobile')} />
                    </RadioGroup>
                </FormGroup>
                <OverrideControls
                    overrideName="allowNextGenMobile"
                    overrideValue={featureSettings.overrides?.allowNextGenMobile ?? ClassLevelOverrideOptions.NoOne}
                    handleUpdateOverrides={handleUpdateOverrides}
                    labelText={t('whoCanOverride')}
                    tooltipText={t('whoCanOverrideTooltip')}
                />

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={featureSettings.options.hasAiTa}
                                onChange={handleUpdateOptionsBool}
                                id="hasAiTa"
                                name="hasAiTa"
                            />
                        }
                        label={t('configuration.allow-ai-ta')}
                    />
                    <OverrideControls
                        overrideName="hasAiTa"
                        overrideValue={featureSettings.overrides?.hasAiTa ?? ClassLevelOverrideOptions.NoOne}
                        handleUpdateOverrides={handleUpdateOverrides}
                        labelText={t('whoCanOverride')}
                        tooltipText={t('whoCanOverrideTooltip')}
                    />
                </FormGroup>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={featureSettings.options.enablePrivateDiscussion}
                                onChange={handleUpdateOptionsBool}
                                id="enablePrivateDiscussion"
                                name="enablePrivateDiscussion"
                            />
                        }
                        label={t('configuration.enable-private-discussion')}
                    />
                    <OverrideControls
                        overrideName="enablePrivateDiscussion"
                        overrideValue={
                            featureSettings.overrides?.enablePrivateDiscussion ?? ClassLevelOverrideOptions.NoOne
                        }
                        handleUpdateOverrides={handleUpdateOverrides}
                        labelText={t('whoCanOverride')}
                        tooltipText={t('whoCanOverrideTooltip')}
                    />
                </FormGroup>

                <StyledFormGroupContainer>
                    <StyledFormGroup>
                        <InputLabel htmlFor="vanityUrl">{t('auth.vanityUrl')}</InputLabel>
                        <TextField
                            value={authSettings.vanityUrl}
                            onChange={handleAuthSettingsChange}
                            id="vanityUrl"
                            name="vanityUrl"
                            size="small"
                        />
                    </StyledFormGroup>
                </StyledFormGroupContainer>

                <StyledFormGroupContainer>
                    <StyledFormGroup>
                        <InputLabel htmlFor="domains">{t('auth.domains')}</InputLabel>
                        <TextField
                            value={authSettings.domains?.join(', ').trim() ?? ''}
                            onChange={handleDomainsChange}
                            id="domains"
                            name="domains"
                            helperText={t('auth.domains-helper-text')}
                            size="small"
                        />
                    </StyledFormGroup>
                </StyledFormGroupContainer>
            </StyledFormGroupContainer>

            <StyledFormGroupContainer>
                <FormGroup>
                    <InputLabel htmlFor="updateMicrosoftLicenseDays">
                        {t('configuration.update-microsoft-license')}
                    </InputLabel>
                    <TextField
                        type="number"
                        sx={{ width: '100px' }}
                        value={classForTeamsSettings.updateMicrosoftLicenseDays}
                        onChange={handleClassForTeamsSettingsChange}
                        id="updateMicrosoftLicenseDays"
                        name="updateMicrosoftLicenseDays"
                    />
                </FormGroup>
            </StyledFormGroupContainer>
            <Title>{t('deprecated-settings')}</Title>
            <StyledFormGroupContainer>
                <StyledFormGroupContainer>
                    <FormGroup>
                        {nonManagedZoomSettingShown && (
                            <>
                                <AdminControlCheckbox
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={featureSettings.forceWebAppChromebooks}
                                            onChange={handleFeatureSettingsChange}
                                            id="forceWebAppChromebooks"
                                            name="forceWebAppChromebooks"
                                        />
                                    }
                                    label={t('user-access.force-web-chromebooks')}
                                />
                            </>
                        )}
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.enforceClassMinimumVersion}
                                    onChange={handleFeatureSettingsChange}
                                    id="enforceClassMinimumVersion"
                                    name="enforceClassMinimumVersion"
                                />
                            }
                            label={t('version-management.enforce-class-minimum-versioning')}
                        />
                    </FormGroup>
                    <FormGroup>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={featureSettings.webApp}
                                    onChange={handleFeatureSettingsChange}
                                    id="webApp"
                                    name="webApp"
                                />
                            }
                            label={t('user-access.allow-web')}
                        />
                        <StyledHelperLink
                            key="knowledge-base"
                            href="https://www.class.com/support/class/admin-guides/web-app/"
                        >
                            {t('user-access.learn-more-class-web')}
                        </StyledHelperLink>
                    </FormGroup>
                    <FormGroup>
                        {!isTeams && (
                            <>
                                <AdminControlCheckbox
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={authSettings.joinMeetingButton}
                                            onChange={handleAuthSettingsChange}
                                            id="joinMeetingButton"
                                            name="joinMeetingButton"
                                            disabled={!isAdmin}
                                        />
                                    }
                                    label={t('auth.joinMeetingButton.text') as string}
                                    hint={t('auth.joinMeetingButton.helperText')}
                                />
                            </>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={featureSettings.options.hasFileAnnotations}
                                    onChange={handleUpdateOptionsBool}
                                    id="hasFileAnnotations"
                                    name="hasFileAnnotations"
                                />
                            }
                            label={t('configuration.allow-file-annotations')}
                        />
                        <OverrideControls
                            overrideName="hasFileAnnotations"
                            overrideValue={
                                featureSettings.overrides?.hasFileAnnotations ?? ClassLevelOverrideOptions.NoOne
                            }
                            handleUpdateOverrides={handleUpdateOverrides}
                            labelText={t('whoCanOverride')}
                            tooltipText={t('whoCanOverrideTooltip')}
                        />
                    </FormGroup>

                    <FormGroup sx={{ mb: 2 }}>
                        <FormLabel id="class-access-select-label" sx={{ color: 'inherit' }}>
                            {t('user-access.class-access.header')}
                        </FormLabel>
                        <FormControl sx={{ maxWidth: 300, marginLeft: 0 }} size="small" variant="outlined">
                            <Select
                                labelId="class-access-select-label"
                                id="classAccess"
                                name="classAccess"
                                value={featureSettings.classAccess}
                                onChange={handleSelectSettingsChange}
                            >
                                <MenuItem value={ClassAccess.All}>{t('user-access.class-access.options.all')}</MenuItem>
                                <MenuItem value={ClassAccess.App}>{t('user-access.class-access.options.app')}</MenuItem>
                                <MenuItem value={ClassAccess.Lti}>{t('user-access.class-access.options.lti')}</MenuItem>
                                <MenuItem value={ClassAccess.None}>
                                    {t('user-access.class-access.options.none')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </FormGroup>
                </StyledFormGroupContainer>
                {!isTeams && (
                    <>
                        <ClassAccessSettings />
                    </>
                )}

                <StyledFormGroupContainer>
                    <FormGroup>
                        {!isTeams && (
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={featureSettings.showMeetingInfo}
                                        onChange={handleFeatureSettingsChange}
                                        id="showMeetingInfo"
                                        name="showMeetingInfo"
                                        disabled={!isAdmin}
                                    />
                                }
                                label={t('configuration.showMeetingInfo') as string}
                                hint={t('configuration.showMeetingHint')}
                            />
                        )}
                    </FormGroup>

                    <FormGroup sx={{ mb: 2 }}>
                        <AdminControlCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={allowVanillaZoom}
                                    onChange={handleUpdateOptionsBool}
                                    id="allowVanillaZoom"
                                    name="allowVanillaZoom"
                                />
                            }
                            label={t('configuration.allow-vanilla-zoom') as string}
                        />
                        <OverrideControls
                            overrideName="allowVanillaZoom"
                            overrideValue={overrides.allowVanillaZoom ?? ClassLevelOverrideOptions.NoOne}
                            handleUpdateOverrides={handleUpdateOverrides}
                            labelText={t('configuration.allow-vanilla-zoom-override')}
                            tooltipText={t('configuration.no-one-allow-vanilla-zoom-tooltip')}
                        />
                    </FormGroup>
                </StyledFormGroupContainer>
            </StyledFormGroupContainer>

            <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />

            <Spacer />

            <SuperAdminTools />
        </>
    );
};

export default SuperAdmin;
